import React, { FunctionComponent, useState } from 'react'
import { Button, Radio, ShowMoreLess } from '@afterpaytouch/core'
import { useGetShowMoreLessHandler } from '../../state'
import { ShippingAddressItem } from '..'
import { ShippingAddress } from '../../model'
import { isUndefinedOrEmpty } from '@afterpay/utils/string'
import { isValidAddress } from '../../utils/profile-validation'
import { MaskedElement } from '@afterpaytouch/integrations/MaskedElement'
import { useTranslation } from '../../utils/convergence'
import { useIsShippingAddressDisabled, useSortedShippingAddresses } from './hooks'

interface ShippingAddressListRedesignedProps {
  onChange: (address: ShippingAddress | undefined) => void
  chosenAddress?: ShippingAddress
  addNewAddressClick: () => void
  addNewAddressClickDisabled: boolean
}

export const ShippingAddressListRedesigned: FunctionComponent<ShippingAddressListRedesignedProps> = ({
  chosenAddress,
  onChange,
  addNewAddressClick,
  addNewAddressClickDisabled,
}) => {
  const { t } = useTranslation()
  const [isAddressListShowingMore, setIsAddressListShowingMore] = useState(false)
  const sortedAddresses = useSortedShippingAddresses(chosenAddress)
  const isShippingAddressDisabled = useIsShippingAddressDisabled()
  const handleShowMoreLess = useGetShowMoreLessHandler('Inline Address')

  const handleAddressChange = (address: ShippingAddress | undefined): void => {
    onChange(address)
    if (isAddressListShowingMore) {
      setIsAddressListShowingMore(false)
    }
  }

  const onShowMoreLessChange = (isShowingMore: boolean): void => {
    setIsAddressListShowingMore(isShowingMore)
  }

  // Only call handler when explicitly clicking ShowMoreLess - we don't want to logEvent when List is automatically reset
  const onShowMoreLessClick = (e: React.SyntheticEvent): void => {
    handleShowMoreLess(!isAddressListShowingMore)
  }

  return (
    <MaskedElement actionName='Select Shipping Address'>
      <Radio.Group
        labelPlacement='Middle'
        labelWidth='Full'
        name='shipping-address'
        value={chosenAddress?.id.toString()}
        onChange={(e) => handleAddressChange(sortedAddresses.find((address) => address.id.toString() === e.target.value))}
        kind='Option'
      >
        <ShowMoreLess onChange={onShowMoreLessChange} onClick={onShowMoreLessClick} isShowingMore={isAddressListShowingMore}>
          {sortedAddresses.map((address, idx) => {
            const showIncompleteAddress = isUndefinedOrEmpty(address?.phoneNumber) || !isValidAddress(address)
            const isDisabled = isShippingAddressDisabled(address)
            return [
              <Radio.Button
                key={address.id}
                value={address.id.toString()}
                id={`shipping-address-${address.id}`}
                kind='Option'
                testNameSpace={`shipping-address-${address.id}`}
                disabled={isDisabled}
                label={() => <ShippingAddressItem {...address} preferred={false} disabled={isDisabled} showIncompleteAddress={showIncompleteAddress} />}
                labelPlacement='Top'
                roundedBorder
                reversed
              />,
            ]
          })}
        </ShowMoreLess>
        <Radio.Footer>
          <Button kind='Secondary' padding='Fluid' testNameSpace='add-new-shipping-address' disabled={addNewAddressClickDisabled} onClick={addNewAddressClick}>
            {t('shippingAddress:addNewShippingAddress')}
          </Button>
        </Radio.Footer>
      </Radio.Group>
    </MaskedElement>
  )
}
